import { $convertFromMarkdownString } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  LexicalNode,
  PASTE_COMMAND,
} from "lexical";
import { useEffect } from "react";
import { PLAYGROUND_TRANSFORMERS } from "../markdownTransformers";
import {
  convertEmptyParagraphHtmlToMarker,
  EMPTY_PARAGRAPH_MARKER,
} from "../preprocessMarkdown";

/**
 * Detects if the given text contains markdown syntax that should be converted.
 * Returns true for tables, headers, and lists.
 */
export function isMarkdownContent(text: string): boolean {
  // Table pattern: line starting with | and containing at least 2 |
  const tablePattern = /^\s*\|.+\|/m;
  // Header pattern: # followed by space at start of line
  const headerPattern = /^#{1,6}\s/m;
  // List pattern: - or * or number followed by . and space at start of line
  const listPattern = /^(\s*[-*]|\s*\d+\.)\s/m;

  return (
    tablePattern.test(text) ||
    headerPattern.test(text) ||
    listPattern.test(text)
  );
}

/**
 * Plugin that intercepts paste events and converts markdown content
 * to Lexical nodes using the PLAYGROUND_TRANSFORMERS.
 *
 * If the pasted content looks like markdown (tables, headers, lists),
 * it will be converted to rich content. Otherwise, default paste behavior is used.
 */
export default function MarkdownPastePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        // If clipboard has rich content (HTML or Lexical format), let default handlers preserve it
        // This ensures copy/paste between Lexical editors maintains rich formatting
        const hasHtmlContent = event.clipboardData?.getData("text/html");
        const hasLexicalContent = event.clipboardData?.getData(
          "application/x-lexical-editor"
        );

        if (hasHtmlContent || hasLexicalContent) {
          // Let Lexical's default paste handler process rich content
          return false;
        }

        const text = event.clipboardData?.getData("text/plain");

        if (!text || !isMarkdownContent(text)) {
          // Not markdown - let default paste behavior handle it
          return false;
        }

        // Prevent default paste
        event.preventDefault();

        editor.update(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return;
          }

          // Remove any selected content first
          selection.removeText();

          // Get the anchor node to insert after
          const anchorNode = selection.anchor.getNode();
          const topLevelNode = anchorNode.getTopLevelElement();

          // Convert markdown string to nodes using root context
          // This is necessary because tables and other block elements
          // can only be created at root level
          const root = $getRoot();
          const originalChildren = root.getChildren();

          // Clear and convert markdown (convert <p></p> markers to internal markers)
          root.clear();
          $convertFromMarkdownString(
            convertEmptyParagraphHtmlToMarker(text),
            PLAYGROUND_TRANSFORMERS
          );

          // Clean up internal markers to leave truly empty paragraphs
          for (const child of root.getChildren()) {
            if (
              $isParagraphNode(child) &&
              child.getTextContent() === EMPTY_PARAGRAPH_MARKER
            ) {
              child.clear();
            }
          }

          // Get the newly created nodes
          const newChildren = root.getChildren();

          // Restore original content and insert new nodes
          root.clear();
          for (const child of originalChildren) {
            root.append(child);
          }

          // Insert converted nodes after the current selection
          if (newChildren.length > 0) {
            if (topLevelNode) {
              // Insert after the current block
              let insertAfter: LexicalNode = topLevelNode;
              for (const newChild of newChildren) {
                insertAfter.insertAfter(newChild);
                insertAfter = newChild;
              }
              // If the original node was empty, remove it
              if (topLevelNode.getTextContent().trim() === "") {
                topLevelNode.remove();
              }
            } else {
              // No top level node, append to root
              for (const newChild of newChildren) {
                root.append(newChild);
              }
            }

            // Move selection to end of inserted content
            const lastChild = newChildren[newChildren.length - 1];
            lastChild.selectEnd();
          }
        });

        return true; // Command handled
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}
